import {computed, observable, makeObservable} from "mobx";
import {
	ViewPreferenceCategory,
	type GlobalBase,
	type OrganizationDto,
	type UpdateOrganizationSettingRequest,
	XyiconFeature,
} from "../../generated/api/base";
import type {AppState} from "../state/AppState";
import {XHRLoader} from "../../utils/loader/XHRLoader";
import {StringUtils} from "../../utils/data/string/StringUtils";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import {
	cleanViewFolderStructureFromAdditionalProps,
	doesItemExistInViewFolderStructure,
	filterViewFolderStructure,
	removeElementFromViewFolderStructureById,
} from "./ViewUtils";
import type {IViewItem, ViewFolderStructure} from "./ViewUtils";
import type {View} from "./View";
import type {Report} from "./Report";

export class Organization {
	private _appState: AppState;
	@observable
	private _data: OrganizationDto = {};

	constructor(data: OrganizationDto, appState: AppState) {
		makeObservable(this);
		this._appState = appState;
		this.applyData(data);
	}

	public applyData(data: OrganizationDto) {
		this._data = {
			...this._data,
			...data,
		};

		if (!this._data.settings) {
			this._data.settings = {};
		}
		if (!this._data.settings.logoFileName) {
			this._data.settings.logoFileName = "";
		}
		if (!this._data.settings.googleMapSettings) {
			this._data.settings.googleMapSettings = {};
		}
		if (!this._data.settings.googleMapSettings.featureLocationSetting) {
			this._data.settings.googleMapSettings.featureLocationSetting = [];
		}
		if (!this._data.settings.defaultViews) {
			this._data.settings.defaultViews = {};
		}
		if (!this._data.settings.globalViews) {
			this._data.settings.globalViews = [];
		}
	}

	@computed
	public get isSSOEnabledForOrganization() {
		return this._data.isSSOEnabledForOrganization;
	}

	@computed
	public get isSSOEnabledForUser() {
		return this._data.isSSOEnabledForUser;
	}

	@computed
	public get id() {
		return this._data.organizationID;
	}

	@computed
	public get name() {
		return this._data.name;
	}

	@computed
	public get ownerUserID() {
		return this._data.ownerUserID;
	}

	@computed
	public get settings() {
		return this._data.settings;
	}

	@computed
	public get lastModifiedAt() {
		return this._data.lastModifiedAt;
	}

	private filterWorkspaceGlobalViews = (view: View | Report) => {
		if (!view.isGlobal) {
			return false;
		}

		return (
			view.ownFeature === XyiconFeature.Report || [XyiconFeature.Boundary, XyiconFeature.Xyicon, XyiconFeature.SpaceEditor].includes(view.itemFeature)
		);
	};

	private getAvailableGlobalViews() {
		return this._appState.actions
			.getAllViews()
			.filter(this.filterWorkspaceGlobalViews)
			.toSorted((a: View | Report, b: View | Report) => StringUtils.sortIgnoreCase(a.name, b.name));
	}

	private getViewFolderStructureItem(view: View | Report): IViewItem {
		return {
			id: view.id,
			category: view.ownFeature === XyiconFeature.Report ? ViewPreferenceCategory.Report : ViewPreferenceCategory.View,
		};
	}

	@computed
	public get globalViews() {
		return this.settings.globalViews as ViewFolderStructure;
	}

	public syncGlobalViewsWithAvailableViews() {
		if (this._appState.actions.getAllViews().length === 0) {
			return;
		}

		const availableViews = this.getAvailableGlobalViews();
		const availableViewIds = availableViews.map((v) => v.id);
		const syncedGlobalViews = filterViewFolderStructure(ObjectUtils.deepClone(this.globalViews), availableViewIds);

		for (const view of availableViews) {
			if (!doesItemExistInViewFolderStructure(syncedGlobalViews, view.id)) {
				syncedGlobalViews.push(this.getViewFolderStructureItem(view));
			}
		}

		for (const viewElement of syncedGlobalViews) {
			const globalViewElement = viewElement as GlobalBase;

			if (!globalViewElement.shared) {
				globalViewElement.shared = {
					userGroupIDs: [],
					userIDs: [],
				};
			}
		}

		if (!ObjectUtils.compare(this.globalViews, syncedGlobalViews)) {
			this._data.settings.globalViews = syncedGlobalViews;
		}
	}

	public setGlobalViews(globalViews: ViewFolderStructure) {
		const availableViews = this.getAvailableGlobalViews();
		const availableViewIds = availableViews.map((v) => v.id);
		const sanitizedGlobalViews = cleanViewFolderStructureFromAdditionalProps(globalViews);

		this._data.settings.globalViews = filterViewFolderStructure(sanitizedGlobalViews, availableViewIds);

		for (const viewElement of this._data.settings.globalViews) {
			if (!viewElement.shared) {
				viewElement.shared = {
					userGroupIDs: [],
					userIDs: [],
				};
			}
		}

		const params: UpdateOrganizationSettingRequest = {
			organizationSettings: this._data.settings,
		};

		return this._appState.app.transport.requestForOrganization({
			url: "organizations/updatesetting",
			method: XHRLoader.METHOD_POST,
			params,
		});
	}

	public saveGlobalViewsToDb() {
		return this.setGlobalViews(this.globalViews);
	}

	public removeElementFromGlobalViews(viewIdToDelete: string) {
		const isDeletionSuccessful = removeElementFromViewFolderStructureById(this.globalViews, viewIdToDelete);

		if (isDeletionSuccessful) {
			return true;
		}

		return false;
	}
}
